import {
  html,
  unsafeSVG,
  customElement,
  property,
  state,
} from '@oskecare/fe-utilities/lib/LitElement/index.js';
import { bound, mixin } from '@oskecare/fe-utilities/lib/decorators';
import OrangeL2Widget from '@oskecare/fe-utilities/lib/OrangeL2Widget';
import { isMobile } from '@oskecare/fe-utilities/lib/Mixins';
import { formatSn } from '@oskecare/fe-utilities/scripts/format';
import { truncateString } from '@oskecare/fe-utilities/scripts/helpers';
import '@oskecare/ui-library/components/tooltip';
import assistanceIcon from '@oskecare/orange-design-system/src/assets/icons/assistance.svg';
import startoverIcon from '@oskecare/orange-design-system/src/assets/icons/startover.svg';
import warningImportantIcon from '@oskecare/orange-design-system/src/assets/icons/warning-important.svg';
import { messageBus } from '../infrastructure.js';
import { config } from '../config.js';

@customElement('osk-sn-picker')
@mixin(isMobile())
class SnPicker extends OrangeL2Widget {
  /**
   * @type {string}
   */
  logSource = 'sn-picker';

  /**
   * @type {string}
   */
  @property({ attribute: 'return-state', type: String })
  accessor returnState = '';

  /**
   * @type {?string}
   */
  @property({ attribute: 'on-demand-topic', type: String })
  accessor onDemandTopic = null;

  /**
   * @type {?CustomerData}
   */
  @state()
  accessor _customerData = null;

  /**
   * @type {boolean}
   */
  @state()
  accessor _isOnDemandVisible = false;

  /**
   * @type {boolean}
   */
  @state()
  accessor _hasDataLoaded = false;

  /**
   * @return {?string}
   */
  get workingSn() {
    return this.infrastructure.customerDataManager.workingSn;
  }

  /**
   * Handler for messageBus event that requests to turn on/off the on-demand snPicker display.
   *
   * @param {{isVisible: boolean}} message
   */
  @bound
  handleOnDemand(message) {
    this._isOnDemandVisible = message.isVisible;
  }

  /**
   * Returns formatted service name in `sn (nickname)` format.
   *
   * @param {string} sn
   * @param {string} nickname
   * @return {string}
   */
  printServiceName(sn, nickname) {
    const printedSn = sn ? formatSn(sn) : '-';
    const printedNickname = this.isMobile ? truncateString(nickname, 15) : nickname;
    return `${printedSn} (${printedNickname || '-'})`;
  }

  /**
   * Handler for changing the service/SN to be worked on/edited
   *
   * @param {Event} event
   */
  @bound
  handleChangeWorkingSn(event) {
    this.infrastructure.customerDataManager.changeWorkingSn({
      workingSn: event.target.value,
      query: this.query,
    });
  }

  /**
   * @see OrangeL2Widget.getData
   * @return {Promise<void>}
   */
  async getData() {
    await super.getData({
      fetch: async () => {
        this._customerData = await this.infrastructure.customerDataManager.getCustomerData();
        this._hasDataLoaded = true;
      },
    });
  }

  /**
   * Checks session storage if the component should still be hidden
   * This information is based on the datetime of the last hide and current datetime
   * If the customer is already authorized the component should be shown regardless.
   *
   * @returns {Boolean}
   */
  isComponentHidden() {
    if (this._customerData?.cnData?.isCustomerAdmin) {
      return false;
    }

    const expirationDate = JSON.parse(sessionStorage.getItem('osk-idauth-hidden'));
    const isHidden = expirationDate && new Date() < Date.parse(expirationDate.expiresAt);

    if (!isHidden) {
      sessionStorage.removeItem('osk-idauth-hidden');
    }

    return isHidden || false;
  }

  /**
   * Sets date, when component should be visible to session storage
   * and call lifecycle `requestUpdate` method to hide it
   */
  hideComponent() {
    const expires = new Date();
    expires.setMinutes(expires.getMinutes() + 30);
    const sessionObject = {
      expiresAt: expires,
    };
    sessionStorage.setItem('osk-idauth-hidden', JSON.stringify(sessionObject));
    this.requestUpdate();
  }

  /**
   * Click handler for hide button
   */
  @bound
  handleHideComponent() {
    this.infrastructure.tagManager.mhubPush({ event: 'action.authDismiss.master' });
    this.hideComponent();
  }

  /**
   * Click handler for verify button
   */
  handleVerifyClick() {
    this.infrastructure.tagManager.mhubPush({ event: 'action.authPerform.master' });
    if (!sessionStorage.getItem('osk-idauth-perform')) {
      sessionStorage.setItem('osk-idauth-perform', true);
    }
    const confirmationLink = new URL(config.authCustomerAdminConfirmLink);
    if (this.returnState) {
      confirmationLink.searchParams.set(
        'state',
        `${this.returnState}${this.query.toString() ? `?${this.query}` : ''}`,
      );
    }
    this.navigate(confirmationLink.toString(), true);
  }

  /**
   * Click handler for error state button, that tries to fetch data again.
   */
  @bound
  handleReload() {
    this.getData();
  }

  /**
   * @returns {TemplateResult}
   */
  renderError() {
    return html`
      <div class="section section--small bg-black megamenu__substrip">
        <div class="container">
          <div class="grid align-items-center justify-content-space-between">
            <div class="grid__col--9 grid__col--sm-8 mb-small mb-lg-none">
              <svg class="icon icon--medium mr-2" aria-hidden="true">
                ${unsafeSVG(warningImportantIcon)}
              </svg>
              <strong> Nepodarilo sa nám načítať informácie. Skúste to, prosím, neskôr. </strong>
            </div>
            <div class="grid__col--3 grid__col--sm-4">
              <div class="buttons sn-picker__buttons mb-none">
                <button @click=${() => this.handleReload()} class="btn btn--small btn--inverse">
                  <svg class="icon align-middle" aria-hidden="true">
                    ${unsafeSVG(startoverIcon)}
                  </svg>
                  <span class="hide-xs-only btn__icon-right">Skúsiť znova</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render working SN selector or authorization prompt in following cases:
   *  - If customer is postpaid and authorized (i.e. customer admin) and have multiple SNs, we render SN picker
   *  - If customer is postpaid and authorized (i.e. customer admin) and have only one SN, we render nothing
   *  - If customer is postpaid and unauthorized (i.e. not customer admin), we render authorization prompt
   *  - If customer is prepaid, we render nothing
   *
   * @returns {?TemplateResult}
   */
  renderUi() {
    if (this._customerData.cnData.isPrepaid) {
      return null;
    }

    if (!this._customerData.cnData.isCustomerAdmin) {
      return html`
        <div class="section section--small bg-black megamenu__substrip">
          <div class="container">
            <div class="grid align-items-center justify-content-space-between">
              <div class="grid__col--lg-fill mb-small mb-lg-none">
                Ak chcete zobraziť všetky svoje služby, zadajte svoje rodné číslo alebo ID kód.
                <osk-tooltip tooltip-id="sn-assistance-tooltip" class="ml-1">
                  <svg
                    slot="tooltip-trigger"
                    class="icon icon--info info-tooltip__icon"
                    aria-hidden="true"
                  >
                    ${unsafeSVG(assistanceIcon)}
                  </svg>
                  <div slot="tooltip-inner">
                    <span class="color-black">
                      Vaše rodné číslo alebo ID kód slúži na dvojfaktorové overenie vašej identity.
                      Zákaznícka zóna Môj Orange vám potom sprístupní všetky svoje funkcie vrátane
                      možnosti spravovať všetky služby, ktoré od Orangeu máte.
                    </span>
                  </div>
                </osk-tooltip>
              </div>
              <div class="grid__col--lg-4">
                <div class="buttons sn-picker__buttons mb-none">
                  <button
                    type="button"
                    class="btn btn--small btn--primary btn--inverse"
                    data-test-id="sn-picker.unauthorized.verify-button"
                    @click=${this.handleVerifyClick}
                  >
                    Pokračovať
                  </button>
                  <button
                    type="button"
                    class="btn btn--small btn--inverse"
                    data-test-id="sn-picker.unauthorized.dismiss-button"
                    @click=${this.handleHideComponent}
                  >
                    Teraz nie
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    if (this._customerData.snDataList.length === 1) {
      return null;
    }

    return html`
      <div class="section section--small bg-black megamenu__substrip">
        <div class="container">
          <div class="bar mb-none">
            <div class="bar__item">
              <label for="snpicker" class="reset-font-weight">Pracujete s číslom</label>
            </div>
            <div class="bar__item bar__item--fill">
              <select
                id="snpicker"
                class="select custom-select-snpicker"
                data-test-id="sn-picker.select-box"
                @change=${this.handleChangeWorkingSn}
              >
                ${this._customerData.snDataList.map(
                  ({ nickname, sn }) => html`
                    <option
                      value=${sn}
                      .selected=${sn === this.workingSn}
                      data-test-id="sn-picker.select-option.${sn}"
                    >
                      ${this.printServiceName(sn, nickname)}
                    </option>
                  `,
                )}
              </select>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Disables shadowRoot for this element.
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Lifecycle method.
   * Called when the element’s DOM has been updated and rendered.
   *
   * Push mHub impression after each path change represented by `returnState` of after the widget has
   * loaded all of its data. This has to be done to prevent race condition of checking on loggedInSnData while getData
   * method setting them is still running.
   * Also manages subscriptions to onDemandTopic of messageBus if any is defined by the property.
   *
   * @param {Map<string, string>} changedProperties - Keys are the names of changed properties; Values are the corresponding previous values.
   */
  updated(changedProperties) {
    super.updated(changedProperties);
    if (
      (changedProperties.has('_hasDataLoaded') || changedProperties.has('returnState')) &&
      this._hasDataLoaded === true &&
      !this._customerData.cnData?.isPrepaid &&
      !this._customerData.cnData?.isCustomerAdmin
    ) {
      this.infrastructure.tagManager.mhubPush({ event: 'action.authImpression.master' });
    }
    if (changedProperties.has('onDemandTopic')) {
      messageBus.unsubscribe(
        'snPickerOnDemand',
        changedProperties.get('onDemandTopic'), // previous value to unsubscribe
        this.handleOnDemand,
      );
      messageBus.subscribe('snPickerOnDemand', this.onDemandTopic, this.handleOnDemand);
    }
  }

  /**
   * Lifecycle function.
   * Called after the element’s DOM has been updated the first time, immediately before `updated` is called.
   *
   * Calls initial `getData`
   */
  firstUpdated() {
    super.firstUpdated();
    this.getData();
  }

  /**
   * @see OrangeL2Widget.render
   * @return {TemplateResult}
   */
  render() {
    if (this.onDemandTopic !== null && !this._isOnDemandVisible) {
      return null;
    }

    if (this.isComponentHidden()) {
      return null;
    }

    return super.render({
      renderLoader: () => html`
        <div class="section section--small bg-black megamenu__substrip">
          <div class="container">
            <div class="grid">
              <div class="grid__col--9 grid__col--md-3">
                <span class="skeleton">&zwnj;</span>
              </div>
            </div>
          </div>
        </div>
      `,
      renderError: () => this.renderError(),
      renderUi: () => this.renderUi(),
    });
  }
}

export default SnPicker;
